
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Eyebrow, Heading, Stack, Text } from '@client/shared/design';
import { useNavigate } from '@client/shared/router/router.tsx';
import {
  fetchMySchedule,
  toMyScheduleView,
  MY_SCHEDULE_QUERY_KEY,
} from './schedule-operations.ts';
import { Calendar } from '@client/shared/calendar/Calendar.tsx';
import { CalendarGrid } from '@client/shared/calendar/CalendarGrid.tsx';
import { calendarEmptyLabel } from '@client/shared/calendar/text.ts';
import { useCalendarView } from '@client/shared/hooks/use-calendar-view.ts';
import { viewerTimeZone } from '@client/shared/lib/viewer-timezone.ts';
import styles from './Schedule.module.css';

function listingHref(listingId: string): string {
  return `/listings/${listingId}`;
}

export function Schedule() {
  const navigate = useNavigate();
  const zone = viewerTimeZone();
  const view = useCalendarView({ zone, range: 'week' });

  const query = useQuery({
    queryKey: MY_SCHEDULE_QUERY_KEY,
    queryFn: ({ signal }) => fetchMySchedule(signal),
    select: toMyScheduleView,
  });

  const hasNoBookings = query.isSuccess && query.data.events.length === 0;

  return (
    <Stack gap={5}>
      <header>
        <Stack gap={1}>
          <Eyebrow>Your schedule</Eyebrow>
          <Heading level={2}>Booked open houses</Heading>
        </Stack>
      </header>

      {query.isPending ? (
        <ScheduleSkeleton />
      ) : query.isError ? (
        <ScheduleError onRetry={() => void query.refetch()} />
      ) : hasNoBookings ? (
        <ScheduleEmpty />
      ) : (
        <Calendar
          zone={zone}
          anchor={view.anchor}
          range={view.range}
          onStep={view.step}
          onToday={view.goToday}
          onRangeChange={view.setRange}
        >
          <CalendarGrid
            events={query.data.events}
            anchor={view.anchor}
            range={view.range}
            emptyLabel={calendarEmptyLabel({ scope: view.range })}
            onSelectEvent={(event) => {
              const listingId = query.data.listingByBookingId.get(event.id);
              if (listingId) navigate(listingHref(listingId));
            }}
          />
        </Calendar>
      )}
    </Stack>
  );
}

function ScheduleEmpty() {
  return (
    <Card pad="lg" className={styles.empty}>
      <Stack gap={3} align="center">
        <span className={styles.emptyGlyph} aria-hidden="true">
          <Chevron />
        </span>
        <Stack gap={1} align="center">
          <Heading level={4} as="h3">
            No open houses on your schedule yet
          </Heading>
          <Text size="sm" tone="soft" className={styles.emptyCopy}>
            When you book an open house it will appear here, and land on your calendar by email.
          </Text>
        </Stack>
      </Stack>
    </Card>
  );
}

function ScheduleError({ onRetry }: { onRetry: () => void }) {
  return (
    <Card pad="lg" className={styles.empty}>
      <Stack gap={3} align="center">
        <Heading level={4} as="h3">
          We couldn’t load your schedule
        </Heading>
        <Text size="sm" tone="soft" className={styles.emptyCopy}>
          Something went wrong loading your open houses. Try refreshing if it keeps happening.
        </Text>
        <Button variant="ghost" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </Stack>
    </Card>
  );
}

function ScheduleSkeleton() {
  return (
    <Stack gap={3} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <Card key={i} pad="md" spine className={styles.skeletonRow}>
          <Stack gap={2}>
            <span className={`${styles.skBlock} ${styles.skTime}`} />
            <span className={`${styles.skBlock} ${styles.skDetail}`} />
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

function Chevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
